import { useState, useEffect } from 'react';
import { Calendar, Check, X, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { API } from '../lib/api';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface TimeEntry {
  entryId: string;
  projectId: string;
  description: string;
  hours: number;
  date: string;
  billable: boolean;
}

interface Timesheet {
  userIdWeek: string;
  sortKey: string;
  status: string;
  totalHours: number;
  weekStart: string;
  userId?: string;
}

function getWeekStartDate(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

export default function TimesheetPage() {
  const [weekStart, setWeekStart] = useState(() => getWeekStartDate(new Date()));
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return formatDate(d);
  });

  useEffect(() => {
    loadData();
  }, [weekStart]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [entriesData, timesheetsData, projectsData] = await Promise.all([
        API.getTimeEntries(),
        API.getTimesheets(),
        API.getProjects(),
      ]);
      const filtered = entriesData.filter((e: TimeEntry) => weekDates.includes(e.date));
      setEntries(filtered);
      setTimesheets(timesheetsData);
      setProjects(projectsData);
    } catch (err) {
      setError('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  };

  const getEntriesForDay = (date: string) => {
    return entries.filter((e) => e.date === date);
  };

  const getTotalForDay = (date: string) => {
    return getEntriesForDay(date).reduce((sum, e) => sum + (e.hours || 0), 0);
  };

  const getWeekTotal = () => {
    return entries.reduce((sum, e) => sum + (e.hours || 0), 0);
  };

  const getTotalBillable = () => {
    return entries.filter((e) => e.billable).reduce((sum, e) => sum + (e.hours || 0), 0);
  };

  const navigateWeek = (direction: number) => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + direction * 7);
    setWeekStart(newDate);
  };

  const goToCurrentWeek = () => {
    setWeekStart(getWeekStartDate(new Date()));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const totalHours = getWeekTotal();
      if (totalHours === 0) {
        setError('Cannot submit empty timesheet');
        setSubmitting(false);
        return;
      }
      await API.submitTimesheet('METADATA', { totalHours, weekStart: formatDate(weekStart) });
      setSuccess('Timesheet submitted for approval');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await API.approveTimesheet('METADATA');
      setSuccess('Timesheet approved');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to approve timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await API.rejectTimesheet('METADATA', 'Please review and resubmit');
      setSuccess('Timesheet rejected');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reject timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const currentWeekStart = formatDate(weekStart);
  const currentTimesheet = timesheets.find((t) => t.weekStart === currentWeekStart);
  const status = currentTimesheet?.status || 'DRAFT';

  const getProjectName = (projectId: string) => {
    const project = projects.find((p) => p.projectId === projectId);
    return project?.name || projectId;
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{success}</span>
          <button onClick={() => setSuccess('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Weekly Timesheet</h2>
            <p className="text-sm text-gray-500 mt-1">
              {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} -{' '}
              {weekEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigateWeek(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={goToCurrentWeek}
                className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg"
              >
                Today
              </button>
              <button
                onClick={() => navigateWeek(1)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Hours</p>
              <p className="text-2xl font-bold text-gray-900">{getWeekTotal().toFixed(1)}</p>
              <p className="text-xs text-gray-500">{getTotalBillable().toFixed(1)}h billable</p>
            </div>
            <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
              status === 'APPROVED' ? 'bg-green-100 text-green-800' :
              status === 'SUBMITTED' ? 'bg-yellow-100 text-yellow-800' :
              status === 'REJECTED' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {status}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  {DAYS.map((day, i) => (
                    <th
                      key={day}
                      className={`px-4 py-3 text-center text-xs font-medium uppercase ${
                        i < 5 ? 'text-gray-700' : 'text-gray-400'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <span>{day}</span>
                        <span className="text-lg font-semibold">{new Date(weekStart.getTime() + i * 86400000).getDate()}</span>
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                      <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No time entries for this week</p>
                      <p className="text-sm mt-1">Start tracking time to see your timesheet here</p>
                    </td>
                  </tr>
                ) : (
                  Object.entries(
                    entries.reduce((acc, entry) => {
                      const key = entry.projectId;
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(entry);
                      return acc;
                    }, {} as Record<string, TimeEntry[]>)
                  ).map(([projectId, projectEntries]) => (
                    <tr key={projectId} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{getProjectName(projectId)}</div>
                      </td>
                      {DAYS.map((_, i) => {
                        const date = weekDates[i];
                        const dayEntries = projectEntries.filter((e) => e.date === date);
                        const dayTotal = dayEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
                        return (
                          <td
                            key={i}
                            className={`px-4 py-4 text-center text-sm ${
                              i >= 5 ? 'bg-gray-50' : ''
                            }`}
                          >
                            {dayTotal > 0 ? (
                              <span className="font-medium text-gray-900">{dayTotal.toFixed(1)}</span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-4 text-center text-sm font-bold text-gray-900">
                        {projectEntries.reduce((sum, e) => sum + (e.hours || 0), 0).toFixed(1)}
                      </td>
                    </tr>
                  ))
                )}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-3 text-sm text-gray-900">Daily Total</td>
                  {DAYS.map((_, i) => (
                    <td key={i} className={`px-4 py-3 text-center text-sm ${i >= 5 ? 'bg-gray-100' : ''}`}>
                      {getTotalForDay(weekDates[i]).toFixed(1)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-center text-sm text-indigo-600">{getWeekTotal().toFixed(1)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {entries.length} time entries this week
          </div>
          <div className="flex gap-3">
            {status === 'DRAFT' && (
              <button
                onClick={handleSubmit}
                disabled={submitting || getWeekTotal() === 0}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Submit for Approval
              </button>
            )}
            {status === 'SUBMITTED' && (
              <>
                <button
                  onClick={handleApprove}
                  disabled={submitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </button>
                <button
                  onClick={handleReject}
                  disabled={submitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
